import { ProcessedTranscript } from '@/lib/transcript-utils';
import { useCallback, useEffect, useState } from 'react';
import { useLiveTranscriptRealtime } from './use-live-transcript-realtime';
import { useTypingAnimation } from './use-typing-animation';

interface UseAiScribeLiveTranscriptConfig {
  scribeJobId?: string;
  enabled?: boolean;
  onTranscriptUpdate: (newTranscriptPart: ProcessedTranscript) => void;
}

export function useAiScribeLiveTranscript({
  scribeJobId,
  enabled = true,
  onTranscriptUpdate,
}: UseAiScribeLiveTranscriptConfig) {
  const [error, setError] = useState<string | null>(null);

  // Typing animation for continuous transcript display
  const {
    displayedText: transcriptContent,
    isTyping,
    addText,
    clearText,
  } = useTypingAnimation({
    typingSpeed: 40, // Slow typing for realistic AI feel
    paragraphDelay: 150, // Brief pause between parts
  });

  // Handle new transcript text from realtime updates
  const handleTranscriptUpdate = useCallback(
    (newPart: ProcessedTranscript) => {
      // Verify we have valid transcript text
      if (!newPart.formattedText || !newPart.formattedText.trim()) {
        console.warn('⚠️ Empty or invalid transcript text received');
        return;
      }

      // ADD: Pass the text portion to the typing animation
      addText(newPart.formattedText);

      onTranscriptUpdate(newPart);
      setError(null); // Clear any previous errors
    },
    [addText, onTranscriptUpdate]
  );

  // Realtime subscription
  const { isConnected, error: realtimeError } = useLiveTranscriptRealtime({
    scribeJobId,
    onTranscriptUpdate: handleTranscriptUpdate,
    enabled: enabled && !!scribeJobId,
  });

  // Clear transcript when scribeJobId changes or disabled
  useEffect(() => {
    clearText();
    setError(null);
  }, [scribeJobId, clearText]);

  // Handle realtime errors
  useEffect(() => {
    if (realtimeError) {
      setError(realtimeError);
    }
  }, [realtimeError]);

  return {
    transcriptContent,
    isTyping,
    isConnected,
    error,
    // Utility methods
    clearTranscript: clearText,
  };
}
